import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TableViewIcon from '@mui/icons-material/TableView';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

import {
  recomputeFromAmount,
  recomputeFromNet,
  recomputeFromTaxAmount,
} from '../popupExpense/helpers';

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'Travel', label: 'Travel' },
  { value: 'Other Direct Costs', label: 'Non-billable Expenses' },
  { value: 'Reimbursable Expenses', label: 'Billable Expenses' },
];

const TEMPLATE_COLUMNS = [
  { key: 'entity', label: 'Entity', required: true },
  { key: 'project', label: 'Project', required: true },
  { key: 'task', label: 'Task', required: true },
  { key: 'category', label: 'Category', required: true },
  { key: 'expense_date', label: 'Expense Date', required: true },
  { key: 'amount', label: 'Amount', required: false },
  { key: 'tax_rate', label: 'Tax Rate', required: false },
  { key: 'tax_amount', label: 'Tax Amount', required: false },
  { key: 'net_amount', label: 'Net Amount', required: false },
  { key: 'notes', label: 'Notes', required: true },
];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const safeNum = (v) => {
  if (typeof v === 'number' && isFinite(v)) return v;
  const s = String(v ?? 0).replace(/,/g, '').trim();
  const n = parseFloat(s);
  return isFinite(n) ? n : 0;
};

const normalizeTaxRate = (val) => {
  if (val === '' || val == null) return '';
  const s = String(val).trim();
  const n = parseFloat(s.replace('%', ''));
  return isNaN(n) ? '' : `${n}%`;
};

const normalizeCategory = (cat) => {
  if (!cat) return '';
  const s = String(cat).trim().toLowerCase();
  if (s === 'travel') return 'Travel';
  if (s === 'other direct costs' || s === 'non-billable expenses') return 'Other Direct Costs';
  if (s === 'reimbursable expenses' || s === 'billable expenses') return 'Reimbursable Expenses';
  return String(cat).trim();
};

const parseExcelDate = (value) => {
  if (!value && value !== 0) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'string') {
    const d = new Date(value);
    return !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : value;
  }
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }
  return String(value);
};

// Mirrors the normalization in PopupExpense
function normalizeProjectsFromCandidates(candidates) {
  const safe = Array.isArray(candidates) ? candidates : [];
  const projects = safe.map((c) => {
    const projectId = c.projectId ?? c.ProjectID ?? c.project ?? c.ProjectName;
    const projectName = c.project ?? c.ProjectName ?? c.projectName ?? String(projectId ?? '');
    const rawTasks = Array.isArray(c.tasks) ? c.tasks : [];
    const tasks = rawTasks
      .map((t) =>
        typeof t === 'string'
          ? { id: t, name: t }
          : { id: t.taskId ?? t.TaskID ?? t.id ?? t.name, name: t.task ?? t.TaskName ?? t.name }
      )
      .filter((t) => t.name && t.name.trim().toLowerCase() !== 'total')
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return { id: String(projectId ?? ''), name: projectName ?? '', tasks };
  });
  projects.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return projects;
}

function validateRow(row, entities) {
  const errors = {};

  // Entity
  if (!String(row.entityName || '').trim()) {
    errors.entity = 'Entity is required';
  } else if (!row.entityId) {
    errors.entity = `"${row.entityName}" not found`;
  }

  // Project
  if (!String(row.project || '').trim()) {
    errors.project = 'Project is required';
  } else if (!row.projectId) {
    errors.project = `"${row.project}" not found`;
  }

  // Task
  if (!String(row.task || '').trim()) {
    errors.task = 'Task is required';
  } else if (row.projectId && !row.taskId) {
    errors.task = `"${row.task}" not found in "${row.project}"`;
  }

  // Category
  if (!String(row.category || '').trim()) {
    errors.category = 'Category is required';
  } else if (!CATEGORIES.some((c) => c.value === row.category)) {
    errors.category = `"${row.category}" is invalid`;
  }

  // Expense date
  if (!String(row.expense_date || '').trim()) {
    errors.expense_date = 'Expense date is required';
  } else if (isNaN(new Date(row.expense_date).getTime())) {
    errors.expense_date = 'Invalid date';
  }

  // Amounts
  if (!row.tax_rate && !row.customTax) {
    errors.amount = 'Tax rate required (or enter a custom tax amount)';
  } else if (safeNum(row.amount) <= 0 && safeNum(row.net_amount) <= 0) {
    errors.amount = 'Amount or net amount must be > 0';
  }

  // Notes
  if (!String(row.notes || '').trim()) {
    errors.notes = 'Notes are required';
  }

  return errors;
}

function buildRowFromRaw(rawRow, index, projectOptions, entities) {
  const category = normalizeCategory(rawRow.category);
  const expense_date = parseExcelDate(rawRow.expense_date);

  // Entity matching by Name (case-insensitive)
  const entityName = String(rawRow.entity || '').trim();
  const matchedEntity = entities.find(
    (e) => (e.Name ?? '').toLowerCase().trim() === entityName.toLowerCase()
  );

  // Amount resolution
  const rawAmount = (rawRow.amount !== '' && rawRow.amount != null) ? safeNum(rawRow.amount) : null;
  const rawNet = (rawRow.net_amount !== '' && rawRow.net_amount != null) ? safeNum(rawRow.net_amount) : null;
  const rawTaxAmt = (rawRow.tax_amount !== '' && rawRow.tax_amount != null) ? safeNum(rawRow.tax_amount) : null;
  const rawTaxRate = (rawRow.tax_rate !== '' && rawRow.tax_rate != null) ? normalizeTaxRate(rawRow.tax_rate) : null;

  let resolved = null;
  let customTax = false;
  let lastBase = 'amount';

  if (rawAmount !== null && rawTaxRate) {
    const r = recomputeFromAmount(rawAmount, rawTaxRate);
    resolved = { amount: safeNum(r.amount), taxAmount: safeNum(r.taxAmount), netAmount: safeNum(r.netAmount) };
    lastBase = 'amount';
  } else if (rawNet !== null && rawTaxRate) {
    const r = recomputeFromNet(rawNet, rawTaxRate);
    resolved = { amount: safeNum(r.amount), taxAmount: safeNum(r.taxAmount), netAmount: safeNum(r.netAmount) };
    lastBase = 'net';
  } else if (rawAmount !== null && rawTaxAmt !== null) {
    const r = recomputeFromTaxAmount(rawAmount, rawTaxAmt, 'amount');
    resolved = { amount: safeNum(r.amount), taxAmount: safeNum(r.taxAmount), netAmount: safeNum(r.netAmount) };
    customTax = true;
    lastBase = 'amount';
  } else if (rawNet !== null && rawTaxAmt !== null) {
    const r = recomputeFromTaxAmount(rawNet, rawTaxAmt, 'net');
    resolved = { amount: safeNum(r.amount), taxAmount: safeNum(r.taxAmount), netAmount: safeNum(r.netAmount) };
    customTax = true;
    lastBase = 'net';
  }

  // Project / task matching by name (case-insensitive)
  const projectName = String(rawRow.project || '').trim();
  const project = projectOptions.find(
    (p) => p.name.toLowerCase().trim() === projectName.toLowerCase()
  );
  const taskName = String(rawRow.task || '').trim();
  const task = project
    ? project.tasks.find((t) => t.name.toLowerCase().trim() === taskName.toLowerCase())
    : null;

  const rowData = {
    id: index,
    entityName,
    entityId: matchedEntity ? String(matchedEntity.StaffID) : null,
    project: projectName,
    task: taskName,
    category,
    expense_date,
    amount: resolved ? resolved.amount : (rawAmount ?? 0),
    tax_rate: resolved && !customTax ? (rawTaxRate || '') : '',
    tax_amount: resolved ? resolved.taxAmount : (rawTaxAmt ?? 0),
    net_amount: resolved ? resolved.netAmount : (rawNet ?? 0),
    customTax,
    lastBase,
    notes: String(rawRow.notes || '').trim(),
    receipt: null,
    projectId: project ? project.id : null,
    taskId: task ? task.id : null,
  };

  rowData.errors = validateRow(rowData, entities);
  return rowData;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BulkImportDialog({ open, onClose, candidates, entities }) {
  const [parsedRows, setParsedRows] = useState([]);
  const [columnError, setColumnError] = useState('');
  const [fileError, setFileError] = useState('');
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  const projectOptions = useMemo(() => normalizeProjectsFromCandidates(candidates), [candidates]);
  const safeEntities = useMemo(() => (Array.isArray(entities) ? entities : []), [entities]);

  const validRowsCount = useMemo(
    () => parsedRows.filter((r) => Object.keys(r.errors).length === 0).length,
    [parsedRows]
  );
  const invalidRowsCount = parsedRows.length - validRowsCount;
  const hasValidationErrors = invalidRowsCount > 0;
  const isValid = parsedRows.length > 0 && !hasValidationErrors;

  // ── Template download ────────────────────────────────────────────────────────
  const createTemplate = useCallback(() => {
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Expenses (template to fill)
    const headers = TEMPLATE_COLUMNS.map((c) => c.key);
    const ws1 = XLSX.utils.aoa_to_sheet([headers]);
    ws1['!cols'] = headers.map((_, i) => ({ wch: i === 0 ? 32 : 26 }));
    XLSX.utils.book_append_sheet(wb, ws1, 'Expenses');

    // Sheet 2 — Projects & Tasks reference
    const refRows = [['Project', 'Task']];
    projectOptions.forEach((p) => {
      if (p.tasks.length === 0) {
        refRows.push([p.name, '']);
      } else {
        p.tasks.forEach((t) => refRows.push([p.name, t.name]));
      }
    });
    const ws2 = XLSX.utils.aoa_to_sheet(refRows);
    ws2['!cols'] = [{ wch: 36 }, { wch: 36 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Projects & Tasks');

    // Sheet 3 — Entities reference
    const entityRows = [['Entity Name']];
    safeEntities.forEach((e) => entityRows.push([e.Name ?? '']));
    const ws3 = XLSX.utils.aoa_to_sheet(entityRows);
    ws3['!cols'] = [{ wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws3, 'Entities');

    XLSX.writeFile(wb, 'bulk_expenses_template.xlsx');
  }, [projectOptions, safeEntities]);

  // ── Upload + parse ────────────────────────────────────────────────────────────
  const handleUpload = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      setColumnError('');
      setFileError('');
      setParsedRows([]);
      setImportResult(null);

      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const wb = XLSX.read(data, { type: 'array', cellDates: true });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const jsonRows = XLSX.utils.sheet_to_json(ws, { defval: '' });

          if (jsonRows.length === 0) {
            setFileError('The file is empty or has no data rows.');
            return;
          }

          // Normalise column names to lowercase snake_case
          const normalized = jsonRows.map((row) => {
            const norm = {};
            Object.entries(row).forEach(([k, v]) => {
              norm[k.toLowerCase().trim().replace(/\s+/g, '_')] = v;
            });
            return norm;
          });

          // Check required columns exist
          const requiredCols = TEMPLATE_COLUMNS.filter((c) => c.required).map((c) => c.key);
          const missing = requiredCols.filter((col) => !(col in normalized[0]));
          if (missing.length > 0) {
            setColumnError(`Missing required columns: ${missing.join(', ')}`);
            return;
          }

          // Drop instruction / hint rows
          const dataRows = normalized.filter((r) => {
            const p = String(r.project || '').trim();
            return p !== '' && p !== '* required' && p !== 'exact project name — see projects & tasks sheet';
          });

          if (dataRows.length === 0) {
            setFileError('No data rows found. Please fill in the template and re-upload.');
            return;
          }

          setParsedRows(
            dataRows.map((raw, i) => buildRowFromRaw(raw, i, projectOptions, safeEntities))
          );
        } catch (err) {
          console.error('Failed to parse file:', err);
          setFileError('Failed to parse file. Please ensure it is a valid Excel (.xlsx) file.');
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [projectOptions, safeEntities]
  );

  // ── Row editing ──────────────────────────────────────────────────────────────
  const updateRow = useCallback(
    (rowId, field, rawValue) => {
      setParsedRows((prev) =>
        prev.map((row) => {
          if (row.id !== rowId) return row;
          let updated = { ...row };

          if (field === 'entity') {
            updated.entityName = rawValue;
            const matched = safeEntities.find((e) => (e.Name ?? '') === rawValue);
            updated.entityId = matched ? String(matched.StaffID) : null;
          } else if (field === 'project') {
            updated.project = rawValue;
            updated.task = '';
            updated.taskId = null;
            const proj = projectOptions.find((p) => p.name === rawValue);
            updated.projectId = proj ? proj.id : null;
          } else if (field === 'task') {
            updated.task = rawValue;
            const proj = projectOptions.find((p) => p.name === updated.project);
            if (proj) {
              const t = proj.tasks.find((t) => t.name === rawValue);
              updated.taskId = t ? t.id : null;
            }
          } else if (field === 'tax_rate') {
            const normalized = rawValue !== '' ? normalizeTaxRate(rawValue) : '';
            updated.customTax = false;
            updated.tax_rate = normalized;
            if (normalized) {
              if (updated.lastBase === 'amount') {
                const r = recomputeFromAmount(safeNum(updated.amount), normalized);
                updated.amount = safeNum(r.amount);
                updated.tax_amount = safeNum(r.taxAmount);
                updated.net_amount = safeNum(r.netAmount);
              } else {
                const r = recomputeFromNet(safeNum(updated.net_amount), normalized);
                updated.amount = safeNum(r.amount);
                updated.tax_amount = safeNum(r.taxAmount);
                updated.net_amount = safeNum(r.netAmount);
              }
            }
          } else if (field === 'amount') {
            const val = safeNum(rawValue);
            updated.lastBase = 'amount';
            if (updated.customTax) {
              const r = recomputeFromTaxAmount(val, safeNum(updated.tax_amount), 'amount');
              updated.amount = safeNum(r.amount);
              updated.tax_amount = safeNum(r.taxAmount);
              updated.net_amount = safeNum(r.netAmount);
            } else if (updated.tax_rate) {
              const r = recomputeFromAmount(val, updated.tax_rate);
              updated.amount = safeNum(r.amount);
              updated.tax_amount = safeNum(r.taxAmount);
              updated.net_amount = safeNum(r.netAmount);
            } else {
              updated.amount = val;
            }
          } else if (field === 'net_amount') {
            const val = safeNum(rawValue);
            updated.lastBase = 'net';
            if (updated.customTax) {
              const r = recomputeFromTaxAmount(val, safeNum(updated.tax_amount), 'net');
              updated.amount = safeNum(r.amount);
              updated.tax_amount = safeNum(r.taxAmount);
              updated.net_amount = safeNum(r.netAmount);
            } else if (updated.tax_rate) {
              const r = recomputeFromNet(val, updated.tax_rate);
              updated.amount = safeNum(r.amount);
              updated.tax_amount = safeNum(r.taxAmount);
              updated.net_amount = safeNum(r.netAmount);
            } else {
              updated.net_amount = val;
            }
          } else if (field === 'tax_amount') {
            const val = safeNum(rawValue);
            updated.customTax = true;
            updated.tax_rate = '';
            const base =
              updated.lastBase === 'amount' ? safeNum(updated.amount) : safeNum(updated.net_amount);
            const r = recomputeFromTaxAmount(base, val, updated.lastBase);
            updated.amount = safeNum(r.amount);
            updated.tax_amount = safeNum(r.taxAmount);
            updated.net_amount = safeNum(r.netAmount);
          } else {
            updated[field] = rawValue;
          }

          updated.errors = validateRow(updated, safeEntities);
          return updated;
        })
      );
    },
    [projectOptions, safeEntities]
  );

  const attachReceipt = useCallback((rowId, file) => {
    setParsedRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, receipt: file } : row))
    );
  }, []);

  const removeReceipt = useCallback((rowId) => {
    setParsedRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, receipt: null } : row))
    );
  }, []);

  // ── Import ───────────────────────────────────────────────────────────────────
  const handleImport = useCallback(async () => {
    setLoading(true);
    setImportResult(null);

    try {
      const formData = new FormData();

      const expenses = parsedRows.map((row) => ({
        staffId: row.entityId,
        projectId: row.projectId,
        taskId: row.taskId,
        date: row.expense_date,
        category: row.category,
        amount: row.amount,
        taxAmount: row.tax_amount,
        taxRate: row.tax_rate ? parseFloat(row.tax_rate) : 0,
        netAmount: row.net_amount,
        notes: row.notes,
      }));

      console.log('[BulkImport] Payload to POST /bulkCreateExpenses:', JSON.stringify(expenses, null, 2));

      formData.append('expenses', JSON.stringify(expenses));

      parsedRows.forEach((row, i) => {
        if (row.receipt) {
          formData.append(`receipt_${i}`, row.receipt, row.receipt.name);
        }
      });

      const resp = await fetch('/api-dev/expenses/bulkCreateExpenses', {
        method: 'POST',
        body: formData,
      });

      const data = await resp.json();

      if (!resp.ok && resp.status !== 207) {
        setImportResult({ networkError: data.error || 'Import failed.' });
        return;
      }

      if (data.failed === 0) {
        // Full success — close dialog and surface a snackbar
        setParsedRows([]);
        setImportResult(null);
        setColumnError('');
        setFileError('');
        onClose();
        setSnackMessage(
          `${data.succeeded} expense${data.succeeded > 1 ? 's' : ''} imported successfully.`
        );
        setSnackOpen(true);
      } else {
        setImportResult(data);
      }
    } catch (err) {
      setImportResult({ networkError: err.message || 'Network error — could not reach the server.' });
    } finally {
      setLoading(false);
    }
  }, [parsedRows]);

  const handleClose = () => {
    if (loading) return;
    setParsedRows([]);
    setColumnError('');
    setFileError('');
    setImportResult(null);
    onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 22px 60px rgba(20, 35, 55, 0.18)',
          background: '#f6f9fc',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(115deg, #0f4c81 0%, #117f8f 55%, #1a9c75 100%)',
          color: 'white',
          py: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TableViewIcon />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Bulk Import Expenses
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              Upload, review, edit, and import in one guided flow
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.2}>
          {/* Instructions + column chips */}
          <Paper
            style={{ marginTop: '16px' }}
            elevation={0}
            sx={{ p: 2, borderRadius: 3, border: '1px solid #d9e6f2', background: 'white' }}
          >
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Download the template, fill it, then upload the file. Columns marked with * are
                required. The template includes reference sheets for valid project, task, and entity
                names.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Fill one amount combination:{' '}
                <strong>Amount + Tax Rate</strong>,{' '}
                <strong>Net Amount + Tax Rate</strong>,{' '}
                <strong>Amount + Tax Amount</strong>, or{' '}
                <strong>Net Amount + Tax Amount</strong>.
              </Typography>
            </Stack>
          </Paper>

          {/* Download / Upload */}
          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 3, border: '1px solid #d9e6f2', background: 'white' }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
              <Button
                onClick={createTemplate}
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Download Template
              </Button>
              <Button
                component="label"
                variant="contained"
                startIcon={<UploadFileIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Upload Excel File
                <input hidden type="file" accept=".xlsx,.xls" onChange={handleUpload} />
              </Button>
            </Stack>
          </Paper>

          {columnError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {columnError}
            </Alert>
          )}
          {fileError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {fileError}
            </Alert>
          )}

          {/* Preview section */}
          {!columnError && parsedRows.length > 0 && (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid #c9e7d9',
                    background: '#f4fcf8',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Valid rows: {validRowsCount}
                    </Typography>
                  </Stack>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid #f2cccc',
                    background: '#fff7f7',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ErrorOutlineIcon color="error" fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Rows with issues: {invalidRowsCount}
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>

              {hasValidationErrors ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Validation issues found. Edit cells directly below to fix them. Import is enabled
                  only when all rows are valid.
                </Alert>
              ) : (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  All rows are valid and ready to import.
                </Alert>
              )}

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid #d9e6f2',
                  background: 'white',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    background: '#f7fbff',
                    borderBottom: '1px solid #e6eff7',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Data Preview and Inline Fixes
                  </Typography>
                </Box>

                <TableContainer sx={{ maxHeight: 440 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          { label: '#', minWidth: 36 },
                          { label: 'Status', minWidth: 56 },
                          { label: 'Entity *', minWidth: 180 },
                          { label: 'Project *', minWidth: 160 },
                          { label: 'Task *', minWidth: 160 },
                          { label: 'Category *', minWidth: 170 },
                          { label: 'Expense Date *', minWidth: 140 },
                          { label: 'Amount', minWidth: 110 },
                          { label: 'Tax Rate', minWidth: 110 },
                          { label: 'Tax Amount', minWidth: 110 },
                          { label: 'Net Amount', minWidth: 110 },
                          { label: 'Notes *', minWidth: 160 },
                          { label: 'Receipt', minWidth: 120 },
                        ].map((col) => (
                          <TableCell
                            key={col.label}
                            sx={{
                              fontWeight: 700,
                              background: '#f0f6fb',
                              minWidth: col.minWidth,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedRows.map((row) => (
                        <RowEditor
                          key={row.id}
                          row={row}
                          projectOptions={projectOptions}
                          entities={safeEntities}
                          onUpdate={updateRow}
                          onAttachReceipt={attachReceipt}
                          onRemoveReceipt={removeReceipt}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
          {/* Import result feedback */}
          {importResult && (
            importResult.networkError ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {importResult.networkError}
              </Alert>
            ) : importResult.succeeded === 0 ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                Import failed for all {importResult.failed} expense{importResult.failed > 1 ? 's' : ''}.{' '}
                {importResult.results?.[0]?.error}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Partial import: {importResult.succeeded} succeeded, {importResult.failed} failed.{' '}
                Failed rows:{' '}
                {importResult.results
                  .filter((r) => !r.success)
                  .map((r) => `#${r.index + 1} — ${r.error}`)
                  .join(' | ')}
              </Alert>
            )
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, background: 'white', borderTop: '1px solid #e6eff7' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {loading
            ? 'Importing…'
            : `Import${parsedRows.length > 0 ? ` (${parsedRows.length} expense${parsedRows.length > 1 ? 's' : ''})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setSnackOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

// ─── FormattedNumberField ─────────────────────────────────────────────────────

function FormattedNumberField({ value, onChange, sx, error }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');

  const display = useMemo(() => {
    const n = Number(value);
    if (!isFinite(n)) return '0.00';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [value]);

  return (
    <TextField
      size="small"
      type="text"
      value={focused ? draft : display}
      onFocus={() => {
        setDraft(Number(value) === 0 ? '' : String(value));
        setFocused(true);
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setFocused(false);
        const n = parseFloat(String(draft).replace(/,/g, ''));
        onChange(isFinite(n) ? n : 0);
      }}
      sx={sx}
      error={!!error}
      inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
    />
  );
}

// ─── RowEditor ────────────────────────────────────────────────────────────────

function RowEditor({ row, projectOptions, entities, onUpdate, onAttachReceipt, onRemoveReceipt }) {
  const fileInputRef = useRef(null);
  const hasErrors = Object.keys(row.errors).length > 0;
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!row.receipt) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(row.receipt);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [row.receipt]);

  const taskOptions = useMemo(() => {
    const proj = projectOptions.find(
      (p) => p.name.toLowerCase() === String(row.project || '').toLowerCase()
    );
    return proj ? proj.tasks : [];
  }, [projectOptions, row.project]);

  const errorSummary = Object.entries(row.errors)
    .map(([f, msg]) => `${f}: ${msg}`)
    .join('\n');

  const errSx = (field) =>
    row.errors[field]
      ? { bgcolor: '#fff5f5', outline: '1px solid #ffbbbb', borderRadius: '4px' }
      : {};

  const selectSx = { minWidth: 148, fontSize: '0.8rem', '& .MuiSelect-select': { py: '6px' } };
  const numSx = { width: 100, '& input': { fontSize: '0.8rem', py: '6px' } };
  const txtSx = { width: 148, '& input': { fontSize: '0.8rem', py: '6px' } };

  const taxRateDisplayValue = row.tax_rate ? row.tax_rate.replace('%', '') : '';

  return (
    <TableRow sx={{ '&:hover': { bgcolor: '#f7fafd' } }}>
      {/* # */}
      <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', px: 1 }}>
        {row.id + 1}
      </TableCell>

      {/* Status */}
      <TableCell sx={{ px: 1 }}>
        <Tooltip
          title={
            hasErrors ? (
              <span style={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>{errorSummary}</span>
            ) : (
              'Valid'
            )
          }
          arrow
        >
          <span>
            {hasErrors ? (
              <ErrorOutlineIcon color="error" fontSize="small" />
            ) : (
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            )}
          </span>
        </Tooltip>
      </TableCell>

      {/* Entity */}
      <TableCell sx={{ px: 0.5, ...errSx('entity') }}>
        <Select
          size="small"
          value={entities.some((e) => e.Name === row.entityName) ? row.entityName : ''}
          onChange={(e) => onUpdate(row.id, 'entity', e.target.value)}
          displayEmpty
          sx={{ ...selectSx, minWidth: 170 }}
        >
          <MenuItem value="">
            <em style={{ fontSize: '0.8rem' }}>Select entity</em>
          </MenuItem>
          {entities.map((e) => (
            <MenuItem key={e.StaffID} value={e.Name} sx={{ fontSize: '0.8rem' }}>
              {e.Name}
            </MenuItem>
          ))}
        </Select>
      </TableCell>

      {/* Project */}
      <TableCell sx={{ px: 0.5, ...errSx('project') }}>
        <Select
          size="small"
          value={projectOptions.some((p) => p.name === row.project) ? row.project : ''}
          onChange={(e) => onUpdate(row.id, 'project', e.target.value)}
          displayEmpty
          sx={selectSx}
        >
          <MenuItem value="">
            <em style={{ fontSize: '0.8rem' }}>Select project</em>
          </MenuItem>
          {projectOptions.map((p) => (
            <MenuItem key={p.id} value={p.name} sx={{ fontSize: '0.8rem' }}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </TableCell>

      {/* Task */}
      <TableCell sx={{ px: 0.5, ...errSx('task') }}>
        <Select
          size="small"
          value={taskOptions.some((t) => t.name === row.task) ? row.task : ''}
          onChange={(e) => onUpdate(row.id, 'task', e.target.value)}
          displayEmpty
          disabled={taskOptions.length === 0}
          sx={selectSx}
        >
          <MenuItem value="">
            <em style={{ fontSize: '0.8rem' }}>Select task</em>
          </MenuItem>
          {taskOptions.map((t) => (
            <MenuItem key={t.id} value={t.name} sx={{ fontSize: '0.8rem' }}>
              {t.name}
            </MenuItem>
          ))}
        </Select>
      </TableCell>

      {/* Category */}
      <TableCell sx={{ px: 0.5, ...errSx('category') }}>
        <Select
          size="small"
          value={CATEGORIES.some((c) => c.value === row.category) ? row.category : ''}
          onChange={(e) => onUpdate(row.id, 'category', e.target.value)}
          displayEmpty
          sx={{ ...selectSx, minWidth: 162 }}
        >
          <MenuItem value="">
            <em style={{ fontSize: '0.8rem' }}>Select category</em>
          </MenuItem>
          {CATEGORIES.map((c) => (
            <MenuItem key={c.value} value={c.value} sx={{ fontSize: '0.8rem' }}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>

      {/* Expense Date */}
      <TableCell sx={{ px: 0.5, ...errSx('expense_date') }}>
        <TextField
          type="date"
          size="small"
          value={row.expense_date}
          onChange={(e) => onUpdate(row.id, 'expense_date', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 138 }}
          inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
        />
      </TableCell>

      {/* Amount */}
      <TableCell sx={{ px: 0.5, ...errSx('amount') }}>
        <FormattedNumberField
          value={row.amount}
          onChange={(v) => onUpdate(row.id, 'amount', v)}
          sx={numSx}
          error={!!row.errors.amount}
        />
      </TableCell>

      {/* Tax Rate */}
      <TableCell sx={{ px: 0.5 }}>
        <TextField
          type="number"
          size="small"
          value={taxRateDisplayValue}
          onChange={(e) => onUpdate(row.id, 'tax_rate', e.target.value)}
          placeholder="e.g. 20"
          sx={numSx}
          inputProps={{ step: '0.01', min: '0', style: { fontSize: '0.8rem', padding: '6px 8px' } }}
          InputProps={{
            endAdornment: (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', pr: 0.5 }}>%</Typography>
            ),
          }}
        />
      </TableCell>

      {/* Tax Amount */}
      <TableCell sx={{ px: 0.5 }}>
        <FormattedNumberField
          value={row.tax_amount}
          onChange={(v) => onUpdate(row.id, 'tax_amount', v)}
          sx={numSx}
        />
      </TableCell>

      {/* Net Amount */}
      <TableCell sx={{ px: 0.5 }}>
        <FormattedNumberField
          value={row.net_amount}
          onChange={(v) => onUpdate(row.id, 'net_amount', v)}
          sx={numSx}
        />
      </TableCell>

      {/* Notes */}
      <TableCell sx={{ px: 0.5, ...errSx('notes') }}>
        <TextField
          size="small"
          value={row.notes}
          onChange={(e) => onUpdate(row.id, 'notes', e.target.value)}
          sx={txtSx}
          inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
        />
      </TableCell>

      {/* Receipt */}
      <TableCell sx={{ px: 0.5 }}>
        {row.receipt ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title={`Open: ${row.receipt.name}`} arrow>
              <Box
                component="a"
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
              >
                {row.receipt.type.startsWith('image/') ? (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="receipt"
                    sx={{
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '1px solid #d0dcea',
                      display: 'block',
                    }}
                  />
                ) : (
                  <PictureAsPdfIcon fontSize="small" color="error" sx={{ cursor: 'pointer' }} />
                )}
              </Box>
            </Tooltip>
            <IconButton size="small" onClick={() => onRemoveReceipt(row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.72rem' }}
            >
              Attach
            </Button>
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) onAttachReceipt(row.id, f);
              }}
            />
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
